import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import API from "../../common/API";
import moment from "moment";

interface ProviderData {
    provider: {
        razon_social: string;
        numero_documento: string;
        estado: string;
        tipo_persona: string;
        forma_juridica: string;
        fecha_creacion_empresa: string | null;
        telefono_comercial: string | null;
        celular_comercial: string | null;
        correo_comercial: string | null;
        direccion: string | null;
        provee: string | null;
        contacto: string | null;
        posicion_contacto: string | null;
        telefono_contacto: string | null;
        correo_contacto: string | null;
        url_certificacion: string | null;
        provincia: string | null;
        municipio: string | null;
        es_mipyme: string | null;
    } | null;
    contracts: any[] | null;
    pagination: {
        page: number;
        limit: number;
        total_results: number | null;
        total_pages: number | null;
    } | null;
}

const RecordProviderContracts = (props: { rnc: string }) => {
    const { rnc } = props;
    const [data, setData] = useState<ProviderData | null>(null);
    const { t } = useTranslation();

    const bindRecords = async () => {
        try {
            const response = await API.getAction(`DGII/ProviderContracts/${rnc}`);
            if (response.status === 200 && response.data?.provider) {
                setData(response.data);
            }
        } catch (error) {
            // silent fail — external data is supplementary
        }
    };

    useEffect(() => {
        if (rnc) bindRecords();
    }, [rnc]);

    if (!data?.provider) return null;

    const { provider, contracts } = data;

    return (
        <Card className="my-4">
            <Card.Header className="display-4">
                {t("Registro de Proveedor del Estado")}
            </Card.Header>
            <Card.Body className="p-4">
                <Row className="mb-3">
                    <Col xs={12} md={6}>
                        <b className="me-2">{t("Razón Social")}</b>
                        {provider.razon_social}
                        <br />
                        <b className="me-2">{t("Tipo de Persona")}</b>
                        {provider.tipo_persona}
                        <br />
                        <b className="me-2">{t("Forma Jurídica")}</b>
                        {provider.forma_juridica}
                        <br />
                        <b className="me-2">{t("Fecha Constitución")}</b>
                        {provider.fecha_creacion_empresa
                            ? moment(provider.fecha_creacion_empresa).format("DD/MM/YYYY")
                            : "-"}
                        <br />
                        <b className="me-2">{t("Provee")}</b>
                        {provider.provee ?? "-"}
                        <br />
                        <b className="me-2">{t("MIPYME")}</b>
                        {provider.es_mipyme ?? "-"}
                    </Col>
                    <Col xs={12} md={6}>
                        <b className="me-2">{t("Estado")}</b>
                        <span className={`badge ${provider.estado === "Activo" ? "bg-success" : provider.estado === "Suspendido" ? "bg-danger" : "bg-secondary"}`}>
                            {provider.estado}
                        </span>
                        <br />
                        <b className="me-2">{t("Dirección")}</b>
                        {[provider.direccion, provider.municipio, provider.provincia].filter(Boolean).join(", ") || "-"}
                        <br />
                        <b className="me-2">{t("Teléfono")}</b>
                        {provider.telefono_comercial ?? provider.celular_comercial ?? "-"}
                        <br />
                        <b className="me-2">{t("Correo")}</b>
                        {provider.correo_comercial ?? "-"}
                        <br />
                        <b className="me-2">{t("Contacto")}</b>
                        {provider.contacto ?? "-"}{provider.posicion_contacto ? ` (${provider.posicion_contacto})` : ""}
                        <br />
                        {provider.url_certificacion && (
                            <a href={provider.url_certificacion} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm mt-2">
                                <i className="fa fa-certificate me-2"></i>
                                {t("Ver Certificación")}
                            </a>
                        )}
                    </Col>
                </Row>

                {contracts && contracts.length > 0 && (
                    <>
                        <hr />
                        <h6 className="mb-3">{t("Contratos")}</h6>
                        <div className="table-responsive">
                            <table className="table table-sm table-hover">
                                <thead>
                                    <tr>
                                        <th>{t("Institución")}</th>
                                        <th>{t("Monto")}</th>
                                        <th>{t("Fecha")}</th>
                                        <th>{t("Descripción")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contracts.map((item: any, index: number) => (
                                        <tr key={index}>
                                            <td>{item.institucion ?? item.institution ?? "-"}</td>
                                            <td>{item.monto ?? item.amount ?? "-"}</td>
                                            <td>{item.fecha ? moment(item.fecha).format("DD/MM/YYYY") : (item.date ?? "-")}</td>
                                            <td>{item.objeto ?? item.descripcion ?? item.description ?? "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default RecordProviderContracts;
